// src/App.tsx
import {
  IonApp,
  IonLabel,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
  isPlatform,
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';

/* Core CSS required for Ionic components */
import '@ionic/react/css/core.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/padding.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/typography.css';
import { HouseLineIcon, IconProps, PawPrintIcon, UserIcon } from '@phosphor-icons/react';
import React from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router';

import { Haptics, ImpactStyle } from '@capacitor/haptics';

import Alert from './components/UI/Alert';
import Spinner from './components/UI/Spinner';
import ProfileCompletionGate from './components/User/ProfileCompletionGate';
import AuthNavigator from './components/Utils/AuthNavigator';
import BackButtonHandler from './components/Utils/BackButtonHandler/BackButtonHandler';
import InitialRedirect from './components/Utils/InitialRedirect';
import NotificationsRealtimeHandler from './components/Utils/NotificationsRealtimeHandler';
import PrivateRoute from './components/Utils/PrivateRoute';
import PushNotificationHandler from './components/Utils/PushNotificationHandler';
import TrackingHandler from './components/Utils/TrackingHandler';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DocumentacionContextProvider } from './context/DocumentacionContext';
import { NotificationsProvider } from './context/NotificationsContext';
import { PetsContextProvider, usePets } from './context/PetsContext';
import { SVFsContextProvider } from './context/SVFsContext';
import { SiniestrosContextProvider } from './context/SiniestrosContext';
import { TutorialProvider } from './context/TutorialContext';
import { UserContextProvider } from './context/UserContext';
import { withPetGuard } from './hocs/withPetGuard';
import { useNotifications } from './hooks/Notifications/useNotifications';
import { useAlert } from './hooks/UI/useAlert';
import Auth from './pages/auth';
import Home from './pages/home';
import NotFound from './pages/not-found';
import Pet from './pages/pet';
import Siniestro from './pages/siniestro';
import Siniestros from './pages/siniestros';
import User from './pages/user';
import './styles/global.scss';
import { ThemeProvider, useTheme } from './theme/ThemeContext';
import { COVERAGE_EXPIRED_ALERT, hasExpiredCoverage, isDawerPet } from './utils/coverage';

const isDesktop = isPlatform('desktop');

export const iconProps: IconProps = {
  weight: 'fill',
};

const PetPageWrapper = withPetGuard(Pet);
const SiniestrosPageWrapper = withPetGuard(Siniestros);
const SiniestroPageWrapper = withPetGuard(Siniestro);
const DAWER_THEME_CLASS = 'company-theme-dawer';

const CompanyThemeHandler: React.FC = () => {
  const { currentPet } = usePets();
  const isDawer = isDawerPet(currentPet);

  React.useEffect(() => {
    document.body.classList.toggle(DAWER_THEME_CLASS, isDawer);

    return () => {
      document.body.classList.remove(DAWER_THEME_CLASS);
    };
  }, [isDawer]);

  return null;
};

const CoverageExpiredStartupAlert: React.FC = () => {
  const { currentPet, isValidating } = usePets();
  const { alertState, showAlert, hideAlert } = useAlert();
  const notifiedPetIdsRef = React.useRef<Set<number>>(new Set());

  React.useEffect(() => {
    if (isValidating) return;

    const expiredPet = hasExpiredCoverage(currentPet) ? currentPet : null;

    if (!expiredPet || notifiedPetIdsRef.current.has(expiredPet.id)) return;

    notifiedPetIdsRef.current.add(expiredPet.id);
    showAlert({
      header: COVERAGE_EXPIRED_ALERT.header,
      message: `La cobertura gratuita de ${expiredPet.nombre} finalizó. Elegí un plan de cobertura para volver a habilitar sus funciones.`,
      buttons: [{ text: 'Entendido' }],
    });
  }, [currentPet, isValidating, showAlert]);

  return (
    <Alert
      isOpen={alertState.isOpen}
      header={alertState.header}
      message={alertState.message}
      buttons={alertState.buttons}
      onDidDismiss={hideAlert}
    />
  );
};

/* ---------------------- */
/* Tabs Layout (con tabs) */
/* ---------------------- */
const TabsLayout: React.FC = () => {
  const location = useLocation();
  const { pets, isValidating, currentPet } = usePets();
  const { state: notificationsState, markAllNotificationsSeen } = useNotifications();
  const isNotificationsSection = location.pathname.startsWith('/tabs/user');
  const isDawer = isDawerPet(currentPet);

  React.useEffect(() => {
    if (!isNotificationsSection || !notificationsState.unreadCount) return;
    markAllNotificationsSeen();
  }, [isNotificationsSection, markAllNotificationsSeen, notificationsState.unreadCount]);

  const handleTabChange = async () => {
    await Haptics.impact({ style: ImpactStyle.Light });
  };

  return (
    <IonTabs
      className={isDawer ? 'tabs-layout tabs-layout--dawer' : 'tabs-layout'}
      onIonTabsDidChange={handleTabChange}
    >
      <IonRouterOutlet id="tabs-outlet">
        <Switch>
          <Route exact path="/tabs/home" component={Home} />
          <Route exact path="/tabs/pet" component={PetPageWrapper} />
          <Route exact path="/tabs/user" component={User} />
          <PrivateRoute
            exact
            path="/tabs/pet/siniestros"
            component={SiniestrosPageWrapper}
          />
          <PrivateRoute
            exact
            path="/tabs/pet/siniestro"
            component={SiniestroPageWrapper}
          />

          <Route exact path="/tabs">
            <Redirect to="/tabs/home" />
          </Route>

          <Route component={NotFound} />
        </Switch>
      </IonRouterOutlet>

      <IonTabBar slot="bottom">
        <IonTabButton tab="home" href="/tabs/home">
          <HouseLineIcon {...iconProps} />
          <IonLabel>Inicio</IonLabel>
        </IonTabButton>

        <IonTabButton
          tab="pet"
          href="/tabs/pet"
          disabled={!pets?.length || (!pets && isValidating)}
        >
          <span className="tab-tutorial-target" data-tut="reactour__pet_icon">
            <PawPrintIcon {...iconProps} />
            <IonLabel>Mascota</IonLabel>
          </span>
        </IonTabButton>

        <IonTabButton tab="user" href="/tabs/user">
          <span className="tab-tutorial-target" data-tut="reactour__user_icon">
            <UserIcon {...iconProps} />
            <IonLabel>Mi Perfil</IonLabel>
          </span>
        </IonTabButton>
      </IonTabBar>
    </IonTabs>
  );
};

/* ------------------------------------------------ */
/* Wrapper para TabsLayout con providers montados   */
/* ------------------------------------------------ */
const TabsLayoutWrapper: React.FC = () => {
  const { authStatus, loading } = useAuth();

  if (loading) {
    return <Spinner fullscreen />;
  }

  if (!authStatus) {
    return <Redirect to="/auth" />;
  }

  return (
    <UserContextProvider>
      <NotificationsProvider>
        <PetsContextProvider>
          <CompanyThemeHandler />
          <ProfileCompletionGate />
          <CoverageExpiredStartupAlert />
          <SiniestrosContextProvider>
            <DocumentacionContextProvider>
              <SVFsContextProvider>
                <TrackingHandler />
                <NotificationsRealtimeHandler />
                <PushNotificationHandler />
                <TabsLayout />
              </SVFsContextProvider>
            </DocumentacionContextProvider>
          </SiniestrosContextProvider>
        </PetsContextProvider>
      </NotificationsProvider>
    </UserContextProvider>
  );
};

/* ---------------------- */
/* App Content (dentro de ThemeProvider) */
/* ---------------------- */
const AppContent: React.FC = () => {
  const { loading } = useTheme();

  if (loading) return <Spinner fullscreen />;

  return (
    <IonApp className={isDesktop ? 'desktop' : undefined}>
      <AuthProvider>
        <TutorialProvider>
          <IonReactRouter>
            <BackButtonHandler />
            <AuthNavigator />

            <IonRouterOutlet id="main">
              <Switch>
                {/* Redirect inteligente basado en auth */}
                <Route exact path="/" component={InitialRedirect} />

                <Route exact path="/auth" component={Auth} />
                <Route path="/tabs" component={TabsLayoutWrapper} />
                <Route component={NotFound} />
              </Switch>
            </IonRouterOutlet>
          </IonReactRouter>
        </TutorialProvider>
      </AuthProvider>
    </IonApp>
  );
};

/* ---------------------- */
/* App principal          */
/* ---------------------- */
const App: React.FC = () => (
  <ThemeProvider>
    <AppContent />
  </ThemeProvider>
);

export default App;
